import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, NEVER, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take } from 'rxjs/operators';

import {
  selectBillAccounts,
  selectBillAccountsLoaded
} from '@amfam/billing/billaccount/data-access/lib/+state/billaccount.selectors';
import { selectAllBillAccounts } from '@amfam/billing/billaccount/data-access/lib/+state/billaccountExp.selectors';
import { env } from '@amfam/environment';
import { DismissNotificationAction } from '@amfam/policy/data-access/lib/+state/documents/documents.actions';
import { selectPolicyDocumentNotifications } from '@amfam/policy/data-access/lib/+state/documents/documents.selectors';
import {
  selectPoliciesLoaded,
  selectPolicyRisks
} from '@amfam/policy/data-access/lib/+state/policies/policies.selectors';
import { selectAllNamedInsuredPolicySummariesRisks } from '@amfam/policy/data-access/lib/+state/summaries/summaries.selectors';
import { GenericProductType } from '@amfam/policy/models/lib/models/generic-product-type';
import { PolicyDisplayOrder } from '@amfam/policy/models/lib/models/policy-constants';
import { PolicySummary } from '@amfam/policy/models/lib/models/policy-summary';
import { PolicyTypeDisplayNameConstants } from '@amfam/policy/models/lib/models/policy-type-display-name-constants';
import { RiskModel } from '@amfam/policy/models/lib/models/risk.model';
import { UserAPIRiskSummary } from '@amfam/policy/models/lib/models/user-api-risk-summary';
import { VehicleTypeConstants } from '@amfam/policy/models/lib/models/vehicle-type-constants';
import type {
  BillAccount,
  CustomerExperienceBilling
} from '@amfam/shared/models/lib/billing/bill-account.model';
import { BillingPaymentPaths } from '@amfam/shared/models/lib/billing/billing-constants';
import type { LogoModel } from '@amfam/shared/models/lib/brand/logo.model';
import { DsHeaderComponent } from '@amfam/shared/ui/ds-header/lib/components/ds-header/ds-header.component';
import {
  LinkClickEvent,
  LinkFormat,
  LinkSliceDefault,
  NavLinkTreeObj,
  SublinkClickEvent
} from '@amfam/shared/ui/ds-header/lib/models/links-model';
import { NotificationObj } from '@amfam/shared/ui/ds-header/lib/models/notification-model';
import { HeaderWrapperService } from '@amfam/shared/ui/ds-header/lib/services/header-wrapper.service';
import { DsRooflineComponent } from '@amfam/shared/ui/ds-roofline/lib/components/ds-roofline/ds-roofline.component';
import { PrettyBillingAcctNum } from '@amfam/shared/ui/pipes/lib/pipes/format.pipe';
import { AccountTypeEnum } from '@amfam/shared/user/lib/+state/user.model';
import {
  selectTypeOfAccountCode,
  selectUserLoaded
} from '@amfam/shared/user/lib/+state/user.selectors';
import {
  selectBrandState,
  selectCusCareEmailHref,
  selectCusCareNumberFullHref,
  selectIsPartner,
  selectPropertyServiceUrl
} from '@amfam/shared/utility/brand/lib/+state/brand.selectors';
import { BrandLinkDataModel } from '@amfam/shared/utility/brand/lib/models/brand.model';
import type {
  ContactDetails,
  ContactUsSection
} from '@amfam/shared/utility/contact-us/lib/models/contact-us.model';
import { ContactUsService } from '@amfam/shared/utility/contact-us/lib/services/contact-us.service';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';
import { selectRouterState } from '@amfam/shared/utility/navigation/lib/+state/router-selectors';
import { ContentDirective } from '@amfam/shared/utility/shared-services/lib/copy/content.directive';
import { CopyService } from '@amfam/shared/utility/shared-services/lib/copy/copy.service';
import { LOGOUT_ORIGINS } from '@amfam/shared/utility/shared-services/lib/core/logout-origin';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';
import { deepEqual } from '@amfam/shared/utility/shared-services/lib/utils/object.utils';
import { DockingBarComponent } from '@amfam/ui-kit/lib/docking-bar/components/docking-bar/docking-bar.component';
import { DockingBarService } from '@amfam/ui-kit/lib/docking-bar/services/docking-bar.service';
import { LoadingSpinnerService } from '@amfam/ui-kit/lib/loading-spinner/services/loading-spinner.service';

import { ForgerockAuthService } from '../../core/auth/forgerock-auth.service';
import { selectLoggedIn } from '../../core/store';
import { SessionTimeoutComponent } from '../session-timeout/session-timeout.component';

const QUERY_PARAM_KEYS = {
  RISK_ID: 'riskId',
  VEHICLE_ID: 'vehicleId'
} as const;

interface SummaryRisk {
  risk: UserAPIRiskSummary & { policyNumber?: string };
  policy: PolicySummary;
}

type LinkItem = SummaryRisk | BillAccount | CustomerExperienceBilling;

interface DetermineLinkFormatParams {
  item: LinkItem;
  linkFormat?: LinkFormat;
  experienceIdLinkPrefix?: string;
  OLSLink?: string;
}

interface BuildNavLinkTreeParams {
  itemArray: NavLinkTreeObj & { subLinkArray: NavLinkTreeObj[] };
  items?: LinkItem[];
  experienceIdLinkPrefix?: string;
  OLSLink?: string;
  postArray?: NavLinkTreeObj[];
}

@Component({
  selector: 'ds-header-wrapper',
  templateUrl: './header-wrapper.component.html',
  styleUrls: ['./header-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    DsHeaderComponent,
    DockingBarComponent,
    SessionTimeoutComponent,
    DsRooflineComponent,
    ContentDirective
  ]
})
export class HeaderWrapperComponent implements OnInit, AfterViewInit, OnDestroy {
  private store = inject(Store);
  private headerWrapperService = inject(HeaderWrapperService);
  private utilService = inject(UtilService);
  private router = inject(Router);
  private forgerockAuthService = inject(ForgerockAuthService);
  private featureService = inject(FeatureFlagService);
  private spinner = inject(LoadingSpinnerService);
  private copyService = inject(CopyService);
  private contactUsService = inject(ContactUsService);
  private destroyRef = inject(DestroyRef);
  private ngZone = inject(NgZone);
  private dockingBarService = inject(DockingBarService);

  readonly fixedwrapper = viewChild.required<ElementRef>('fixedwrapper');
  private headerResizeObserver?: ResizeObserver;
  // A valid ForgeRock session is what makes the user logged in. Don't key this off
  // forgerockId: it is only filled from the digital account's ciamUsers, which can be empty.
  private readonly loggedIn$: Observable<boolean> = this.store.select(selectLoggedIn);
  readonly loggedIn = this.store.selectSignal(selectLoggedIn);

  // Header asset info
  readonly homeUrl = computed(() => {
    const { isPartner, data } = this.brandInfo().brandState;
    if (!isPartner) return 'overview';
    return data.landingPageUrl ? `${this.experienceIdLinkPrefix()}/overview` : '';
  });
  readonly skipContentFocus = signal(false);
  private skipContentHeight = 48; // Deafault height for Skip to Content link
  readonly headerHeight = signal(80); // Default height for header
  readonly headerOffset = signal(0); // Default offset for header
  readonly showHeader = signal(true);

  // Observables
  readonly dataLoaded$: Observable<boolean> = combineLatest([
    this.store.select(selectPoliciesLoaded),
    this.store.select(selectBillAccountsLoaded),
    this.store.select(selectUserLoaded)
  ]).pipe(
    map(
      ([policiesLoaded, billAccountsLoaded, userLoaded]) =>
        policiesLoaded && billAccountsLoaded && userLoaded
    )
  );
  readonly isPartner = this.store.selectSignal(selectIsPartner);
  // Get all of the store data needed to construct the links
  private readonly headerData$: Observable<{
    risks: RiskModel[];
    summaryRisks: SummaryRisk[];
    billAccounts: BillAccount[];
    billAccountsFromExpApi: CustomerExperienceBilling[];
    user: { typeOfAccountCode: string };
  }> = combineLatest([
    this.store.select(selectPolicyRisks),
    this.store.select(selectAllNamedInsuredPolicySummariesRisks), // create a selector that provides {details, risk1}, {details, risk2} from {details, risks: [risk1, risk2]}
    this.store.select(selectBillAccounts),
    this.store.select(selectAllBillAccounts),
    this.store.select(selectTypeOfAccountCode)
  ]).pipe(
    map(([risks, summaryRisks, billAccounts, billAccountsFromExpApi, typeOfAccountCode]) => ({
      risks,
      summaryRisks,
      billAccounts,
      billAccountsFromExpApi,
      user: { typeOfAccountCode }
    }))
  );
  readonly headerLogo = computed<LogoModel | null>(
    () => this.brandInfo().brandState.data.brandHeaderLogo
  );
  readonly notifications = this.store.selectSignal(selectPolicyDocumentNotifications);
  // Kept as Observables for `| async`: the link arrays include the contact-details HTTP call, and
  // navigating to an overview page rebuilds them (re-subscribing that call). The signal holds the
  // current build so the rebuild is still a fresh subscription.
  readonly linkArray$ = signal<Observable<NavLinkTreeObj[]>>(NEVER);

  private readonly brandInfo$: Observable<BrandLinkDataModel> = combineLatest([
    this.store.select(selectBrandState),
    this.store.select(selectCusCareNumberFullHref),
    this.store.select(selectCusCareEmailHref)
  ]).pipe(
    map(([brandState, cusCareNumberFullHref, cusCareEmailHref]) => ({
      brandState,
      cusCareNumberFullHref,
      cusCareEmailHref
    }))
  );
  readonly brandInfo = toSignal(this.brandInfo$, { requireSync: true });
  readonly contactDetails$: Observable<ContactDetails> = this.loggedIn$.pipe(
    filter(loggedIn => loggedIn),
    take(1),
    switchMap(() => this.contactUsService.getContactDetails())
  );
  private readonly experienceIdLinkPrefix$: Observable<string> = this.store
    .select(selectBrandState)
    .pipe(
      map(brandState => {
        // Only add experience to url if the partner is not the base deployment partner.
        if (brandState.experienceId && brandState.customerGroupId !== env.myAccountBuildGroupId) {
          return `/${brandState.experienceId}`;
        } else {
          return '';
        }
      })
    );
  private readonly experienceIdLinkPrefix = toSignal(this.experienceIdLinkPrefix$, {
    requireSync: true
  });
  private readonly olsLink$: Observable<string> = this.store.select(selectPropertyServiceUrl);
  readonly mobileMenuActive = toSignal(this.headerWrapperService.mobileMenuActive$, {
    requireSync: true
  });
  private isGlobalOutage = false;

  ngOnInit() {
    this.headerWrapperService.notificationToDismiss$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(notification => {
        this.notificationDismissClicked(notification);
      });

    this.store
      .select(selectRouterState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(routerState => {
        this.headerWrapperService.setRouterStateUrl(routerState.state.url ?? '');
      });

    this.dataLoaded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(dataLoaded => {
      this.headerWrapperService.setLoaded(dataLoaded);
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (
          event.url.includes('/overview') ||
          event.url === '/overview' ||
          event.url === '/paperless/signup' ||
          event.url === '/paperless/sign-up'
        ) {
          this.linkArray$.set(this.buildAllLinkArrays().pipe(takeUntilDestroyed(this.destroyRef)));
        }
      }
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (
          event.url.includes('/overview') ||
          event.url === '/overview' ||
          event.url.includes('/underwriting-companies') ||
          event.url.includes('/legal-notice') ||
          /^\/billing\/\d{11}$/.test(event.url)
        ) {
          this.showHeader.set(false);
        } else {
          this.showHeader.set(true);
        }
      }
    });

    // Build linkArrays and send to the servic
    this.linkArray$.set(this.buildAllLinkArrays().pipe(takeUntilDestroyed(this.destroyRef)));
  }

  ngAfterViewInit() {
    // Subscribe to route changes and recalculate
    // Filter only for NavigationEnd
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateHeaderHeight();
      }
    });

    // Listen for window resize events
    window.addEventListener('resize', () => {
      this.updateHeaderHeight();
    });

    // Catches header height changes not driven by route nav or window resize
    // (e.g. content-driven changes like notifications or roofline showing/hiding)
    this.headerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => this.updateHeaderHeight());
    });
    this.headerResizeObserver.observe(this.fixedwrapper().nativeElement);

    this.dockingBarService.dockEmpty$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateHeaderHeight());
  }

  // Helper method to update the header height
  private updateHeaderHeight() {
    window.setTimeout(() => {
      this.headerHeight.set(this.fixedwrapper().nativeElement.getBoundingClientRect().height);
      document.documentElement.style.setProperty(
        '--myaccount-header-height',
        `${this.headerHeight()}px`
      );
    }, 0);
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.updateHeaderHeight);
    this.headerResizeObserver?.disconnect();
  }

  // When the skip content link has focus
  focusHandler() {
    // Send update to service
    this.headerWrapperService.setSkipContentFocus(true);

    this.skipContentFocus.set(true);
    this.headerHeight.update(height => height + this.skipContentHeight);
    this.headerOffset.set(this.skipContentHeight);
    this.utilService.scrollToTop();
  }

  // When the skip content link has lost focus
  blurHandler() {
    // Send update to service
    this.headerWrapperService.setSkipContentFocus(false);

    this.skipContentFocus.set(false);
    this.headerHeight.update(height => height - this.skipContentHeight);
    this.headerOffset.set(0);
  }

  // Set focus to main content
  focusMainContent() {
    this.headerWrapperService.setFocusMainContent(true);
    // scroll to top otherwise the window is off by the height of the skip content block
    this.utilService.scrollToTop();
  }

  linkClicked({ link }: LinkClickEvent) {
    // Event is also available if needed. Access it by deconstructing the incoming object like: {link, event}
    if (link?.clickFunction) {
      link.clickFunction();
    }
    this.utilService.scrollToTop();
  }

  sublinkClicked({ sublink }: SublinkClickEvent) {
    // Event is also available if needed. Access it by deconstructing the incoming object like: {link, event}
    if (sublink?.clickFunction) {
      sublink.clickFunction();
    }
    this.utilService.scrollToTop();
  }

  notificationDismissClicked(notification: NotificationObj) {
    if (notification.ids?.length) {
      notification.ids.forEach((documentId: string) => {
        this.store.dispatch(DismissNotificationAction(documentId));
      });
    }
  }

  private getPolicySortOrder(summaryRisk: SummaryRisk): number {
    const policy = summaryRisk?.policy;
    if (!policy) return PolicyDisplayOrder.DEFAULT;

    // Commercial policies go last
    if (policy.isCommercial) return PolicyDisplayOrder.COMMERCIAL;

    // Check for watercraft FIRST by product description (before checking AUTO)
    // This is important because watercraft might also have policyTypeDisplayName === 'AUTO'
    if (policy.productDescription?.toLowerCase() === VehicleTypeConstants.WATERCRAFT)
      return PolicyDisplayOrder.WATERCRAFT;

    // Check by policy type display name
    if (policy.policyTypeDisplayName === PolicyTypeDisplayNameConstants.AUTO)
      return PolicyDisplayOrder.VEHICLES;
    if (policy.policyTypeDisplayName === PolicyTypeDisplayNameConstants.HOME)
      return PolicyDisplayOrder.HOME;
    if (policy.policyTypeDisplayName === PolicyTypeDisplayNameConstants.UMBRELLA)
      return PolicyDisplayOrder.UMBRELLA;
    if (policy.policyTypeDisplayName === PolicyTypeDisplayNameConstants.LIFE)
      return PolicyDisplayOrder.LIFE;

    // Check by generalized product type as fallback
    if (policy.generalizedProductType === GenericProductType.Auto)
      return PolicyDisplayOrder.VEHICLES;
    if (policy.generalizedProductType === GenericProductType.Home) return PolicyDisplayOrder.HOME;
    if (policy.generalizedProductType === GenericProductType.Umbrella)
      return PolicyDisplayOrder.UMBRELLA;
    if (policy.generalizedProductType === GenericProductType.Life) return PolicyDisplayOrder.LIFE;

    // Default - put at the end
    return PolicyDisplayOrder.DEFAULT;
  }

  private buildRiskLinkArray(): Observable<NavLinkTreeObj | undefined> {
    let riskLinkArray: NavLinkTreeObj & { subLinkArray: NavLinkTreeObj[] };
    return combineLatest([this.experienceIdLinkPrefix$, this.headerData$, this.olsLink$]).pipe(
      map(([experienceIdLinkPrefix, state, OLSLink]) => {
        {
          riskLinkArray = {
            linkText: 'Policies',
            linkFormat: LinkFormat.POLICY,
            linkTestId: 'navItemPolicyOverviewLink',
            submenuTestId: 'navItemPolicySubmenu',
            mobileLinkTestId: 'mobileNavItemPolicyOverviewLink',
            mobileSubmenuTestId: 'mobileNavItemPolicySubmenu',
            mobileMenuItemOrder: 3,
            showOnNavigationMenu: true,
            linkClasses: 'nav-item-policyoverview-link',
            subLinkArray: []
          };
        }
        if (riskLinkArray?.subLinkArray) {
          // Sort the risks by policy type: Vehicles, Watercraft, Home, Umbrella, Life, Commercial
          const sortedRisks = [...state.summaryRisks].sort((a, b) => {
            const orderA = this.getPolicySortOrder(a);
            const orderB = this.getPolicySortOrder(b);
            return orderA - orderB;
          });

          return this.buildNavLinkTreeObjArray({
            itemArray: riskLinkArray,
            items: sortedRisks,
            experienceIdLinkPrefix,
            OLSLink
          });
        }
      })
    );
  }

  private buildBillAccountLinkArray(): Observable<NavLinkTreeObj | undefined> {
    let billAccountLinkArray: NavLinkTreeObj & { subLinkArray: NavLinkTreeObj[] };
    return combineLatest([this.experienceIdLinkPrefix$, this.headerData$]).pipe(
      map(([experienceIdLinkPrefix, state]) => {
        {
          billAccountLinkArray = {
            linkText: 'Billing & Payments',
            linkRoute: experienceIdLinkPrefix + '/billing',
            linkFormat: LinkFormat.BILLING,
            linkTestId: 'navItemBillingOverviewLink',
            submenuTestId: 'navItemBillingSubmenu',
            mobileLinkTestId: 'mobileNavItemBillingOverviewLink',
            mobileSubmenuTestId: 'mobileNavItemBillingSubmenu',
            mobileMenuItemOrder: 2,
            showOnNavigationMenu: true,
            linkClasses: 'nav-item-billingoverview-link',
            subLinkArray: [
              {
                linkText: 'Billing Overview',
                linkRoute: experienceIdLinkPrefix + '/billing',
                linkIcon: 'icon-view-all',
                linkTestId: 'subnavItemBillingOverviewLink',
                mobileLinkTestId: 'mobileSubnavItemBillingOverviewLink'
              }
            ],
            linkTreeSliceObj: {
              linkCount: LinkSliceDefault,
              linkText: 'View All',
              linkRoute: experienceIdLinkPrefix + '/billing',
              linkIcon: 'icon-view-all',
              linkTestId: 'subnavItemBillingViewAllLink',
              mobileLinkTestId: 'mobileSubnavItemBillingViewAllLink'
            }
          };
        }
        if (billAccountLinkArray?.subLinkArray) {
          return this.buildNavLinkTreeObjArray({
            itemArray: billAccountLinkArray,
            items: [
              ...state.billAccounts.filter(account => !account.billingDataIncomplete),
              ...state.billAccountsFromExpApi
                .filter(account => !!account.billAccountInfo?.autopay)
                .map(account => ({ ...account, fromExpApi: true }))
            ]
          });
        }
      })
    );
  }

  private buildClaimLinkArray(): Observable<NavLinkTreeObj> {
    return combineLatest([this.experienceIdLinkPrefix$, this.headerData$]).pipe(
      map(([experienceIdLinkPrefix, state]) => {
        const claimsRoute = experienceIdLinkPrefix + '/claimsV2';
        const fileAClaimRoute = experienceIdLinkPrefix + '/claimsV2/risk-selection';

        const claimLinkArray: NavLinkTreeObj = {
          linkText: 'Claims',
          linkRoute: claimsRoute,
          linkFormat: LinkFormat.CLAIM,
          linkTestId: 'navItemClaimOverviewLink',
          submenuTestId: 'navItemClaimSubmenu',
          mobileLinkTestId: 'mobileNavItemClaimOverviewLink',
          mobileSubmenuTestId: 'mobileNavItemClaimSubmenu',
          mobileMenuItemOrder: 4,
          showOnNavigationMenu: true,
          linkClasses: 'navitem-claimoverview-link',
          subLinkArray: [
            {
              linkText: 'Claims Overview',
              linkRoute: claimsRoute,
              linkTestId: 'subnavItemClaimOverviewLink',
              mobileLinkTestId: 'mobileSubnavItemClaimOverviewLink',
              linkIcon: 'icon-claims'
            }
          ]
        };

        // File a Claim link
        if (state.user.typeOfAccountCode === AccountTypeEnum.Customer) {
          claimLinkArray.subLinkArray?.push({
            linkText: 'File a Claim',
            linkRoute: fileAClaimRoute,
            linkTestId: 'subnavItemClaimReportLink',
            mobileLinkTestId: 'mobileSubnavItemClaimReportLink',
            linkIcon: 'icon-file-claim'
          });
        }

        // Claims FAQ link
        claimLinkArray.subLinkArray?.push({
          linkText: 'Claims FAQ',
          linkRoute: experienceIdLinkPrefix + '/faq/claims',
          linkTestId: 'subnavItemClaimFaqLink',
          mobileLinkTestId: 'mobileSubnavItemClaimFaqLink',
          linkIcon: 'icon-faqs'
        });

        return claimLinkArray;
      })
    );
  }

  // Determine the type of link and format it accordingly
  private determineLinkFormatType(link: DetermineLinkFormatParams): NavLinkTreeObj | undefined {
    switch (link?.linkFormat) {
      case LinkFormat.POLICY:
        return this.formatPolicyRiskLinkForUserApi(
          link.item as SummaryRisk,
          link.experienceIdLinkPrefix ?? '',
          link.OLSLink ?? ''
        );
      case LinkFormat.BILLING:
        return this.formatBillAccountLink(
          link.item as BillAccount | CustomerExperienceBilling,
          link.experienceIdLinkPrefix ?? ''
        );
    }
  }

  private formatPolicyRiskLinkForUserApi(
    summaryRisk: SummaryRisk,
    experienceIdLinkPrefix = '',
    OLSLink = ''
  ): NavLinkTreeObj {
    const riskTestId = summaryRisk.risk.vehicleId
      ? summaryRisk.risk.vehicleId
      : summaryRisk.risk.policyNumber;

    const policyDetailsRoute = String(summaryRisk.policy.policyDetailsRoute).toLowerCase();
    const riskIdentifier = summaryRisk.risk.riskId || summaryRisk.risk.vehicleId;
    const queryParamKey = QUERY_PARAM_KEYS.RISK_ID;
    const queryParams = riskIdentifier ? { [queryParamKey]: riskIdentifier } : {};

    if (summaryRisk.policy.takeToOLS) {
      // OLS Link - use linkExternalUrl for external navigation
      return {
        linkText: summaryRisk.risk.riskDescription,
        linkExternalUrl: OLSLink + '&policyNumber=' + summaryRisk.policy.policyNumber,
        linkTarget: '_self',
        queryParams,
        linkTestId: 'subnavItemPolicy-' + riskTestId + '-Link',
        mobileLinkTestId: 'mobileSubnavItemPolicy-' + riskTestId + '-Link',
        linkIcon: summaryRisk.policy.iconType
      };
    } else {
      // policy details link within MyAccount - use linkRoute for internal navigation
      return {
        linkText: summaryRisk.risk.riskDescription,
        linkRoute:
          experienceIdLinkPrefix +
          '/policies/' +
          policyDetailsRoute +
          '/' +
          summaryRisk.policy.policyNumber,
        queryParams,
        linkTestId: 'subnavItemPolicy-' + riskTestId + '-Link',
        mobileLinkTestId: 'mobileSubnavItemPolicy-' + riskTestId + '-Link',
        linkIcon: summaryRisk.policy.iconType
      };
    }
  }

  private formatBillAccountLink(
    billAccount: BillAccount | CustomerExperienceBilling,
    experienceIdLinkPrefix: string
  ): NavLinkTreeObj {
    // Handle different property names between BillAccount and CustomerExperienceBilling
    const isExpApi = 'fromExpApi' in billAccount;
    const accountNumber = isExpApi
      ? (billAccount as CustomerExperienceBilling).billingAccountNumber
      : (billAccount as BillAccount).billAccountNumber;
    const billingPath = isExpApi
      ? BillingPaymentPaths.BILLING_SECTION_PAGE_EXP
      : BillingPaymentPaths.BILLING_DETAIL_PAGE;

    return {
      linkText:
        (billAccount as BillAccount).billingPreferences?.accountNickName ||
        new PrettyBillingAcctNum().transform(accountNumber),
      linkRoute: experienceIdLinkPrefix + billingPath + '/' + accountNumber,
      linkTestId: 'subnavItemBilling-' + accountNumber + '-Link',
      mobileLinkTestId: 'mobileSubnavItemBilling-' + accountNumber + '-Link',
      linkIcon: 'icon-payments'
    };
  }

  private formatNavLinkTreeSliceObj(link: NavLinkTreeObj): NavLinkTreeObj {
    return {
      linkText: link.linkText,
      linkRoute: link.linkRoute,
      linkIcon: link.linkIcon,
      linkTestId: link.linkTestId,
      mobileLinkTestId: link.mobileLinkTestId,
      dividerAfter: link.dividerAfter
    };
  }

  private buildNavLinkTreeObjArray(link: BuildNavLinkTreeParams): NavLinkTreeObj {
    // Check if we're limiting the links displayed
    const linkTreeSliceObjCount = link.itemArray?.linkTreeSliceObj?.linkCount;
    // If we have limited links, only push those into the array
    if (link.items?.length) {
      if (linkTreeSliceObjCount) {
        link.items.forEach((item: LinkItem, index: number) => {
          if (index + 1 <= linkTreeSliceObjCount) {
            link.itemArray.subLinkArray.push(
              this.determineLinkFormatType({
                item,
                linkFormat: link.itemArray.linkFormat,
                experienceIdLinkPrefix: link.experienceIdLinkPrefix,
                OLSLink: link.OLSLink
              }) as NavLinkTreeObj
            );
          }
        });

        // If total links are greater than the link limit prop, add the linkTreeSliceObj props
        if (link.items.length > linkTreeSliceObjCount && link.itemArray.linkTreeSliceObj) {
          link.itemArray.subLinkArray.push(
            this.formatNavLinkTreeSliceObj(link.itemArray.linkTreeSliceObj)
          );
        }
        // Else, push every link into the array
      } else {
        link.items.forEach((item: LinkItem) => {
          link.itemArray.subLinkArray.push(
            this.determineLinkFormatType({
              item,
              linkFormat: link.itemArray.linkFormat,
              experienceIdLinkPrefix: link.experienceIdLinkPrefix,
              OLSLink: link.OLSLink
            }) as NavLinkTreeObj
          );
        });
      }
    }

    // Some link objects have links after the formatted links, lets add those
    if (link.postArray) {
      link.postArray.forEach((postItem: NavLinkTreeObj) => {
        link.itemArray.subLinkArray.push(postItem);
      });
    }
    return link.itemArray;
  }

  private buildContactSubLinkArray(
    contactUs: ContactUsSection[] = [],
    claimsMessage?: string
  ): NavLinkTreeObj[] {
    const sections = contactUs.flatMap((contactSection, index): NavLinkTreeObj[] => {
      const suffix = index === 0 ? '' : `-${index}`;
      const items: NavLinkTreeObj[] = [];

      if (contactSection?.label) {
        items.push({
          linkText: contactSection.label,
          textOnly: true,
          listClasses: 'customer-care-hours no-border no-padding contact-label',
          linkTestId: `navItemCustomerCareLabelLink${suffix}`,
          mobileLinkTestId: `mobileNavItemCustomerCareLabelLink${suffix}`
        });
      }

      items.push({
        linkText: contactSection?.phoneNumbers.customerCareNumber ?? '',
        linkExternalUrl: `tel:${contactSection?.phoneNumbers.customerCareNumber ?? ''}`,
        linkTarget: 'partner',
        linkIcon: 'icon-call',
        listClasses: 'no-border no-padding',
        linkTestId: `navItemCustomerCarePhoneLink${suffix}`,
        mobileLinkTestId: `mobileNavItemCustomerCarePhoneLink${suffix}`
      });

      if (contactSection?.hoursOfOperation.customerCareHours_Weekday) {
        items.push({
          linkText: contactSection.hoursOfOperation.customerCareHours_Weekday,
          textOnly: true,
          subLinkType: 'weekday',
          listClasses: 'customer-care-hours no-border no-padding',
          linkTestId: `navItemCustomerCareHoursWeekdayLink${suffix}`,
          mobileLinkTestId: `mobileNavItemCustomerCareHoursWeekdayLink${suffix}`
        });
      }

      if (contactSection?.hoursOfOperation.customerCareHours_Weekend) {
        items.push({
          linkText: contactSection.hoursOfOperation.customerCareHours_Weekend,
          textOnly: true,
          subLinkType: 'weekend',
          listClasses: 'customer-care-hours no-border no-padding',
          linkTestId: `navItemCustomerCareHoursWeekendLink${suffix}`,
          mobileLinkTestId: `mobileNavItemCustomerCareHoursWeekendLink${suffix}`
        });
      }

      const lastItem = items[items.length - 1];
      if (lastItem && index < contactUs.length - 1) {
        lastItem.dividerAfter = true;
      }

      return items;
    });

    if (!claimsMessage) {
      return sections;
    }

    const lastSection = sections[sections.length - 1];
    if (lastSection) {
      lastSection.dividerAfter = true;
    }
    return [
      ...sections,
      {
        linkText: claimsMessage,
        textOnly: true,
        listClasses: 'customer-care-hours no-border no-padding',
        linkTestId: 'navItemCustomerCareClaimsMessage',
        mobileLinkTestId: 'mobileNavItemCustomerCareClaimsMessage'
      }
    ];
  }

  private buildContactLinkArray(): Observable<NavLinkTreeObj> {
    // contactDetails$ is an HTTP call that only starts once loggedIn$ is true. Without a seed,
    // buildAllLinkArrays' combineLatest held back the entire nav until /contact-us answered.
    return combineLatest([this.brandInfo$, this.contactDetails$.pipe(startWith(undefined))]).pipe(
      map(([brandInfo, contactDetails]): NavLinkTreeObj => {
        {
          if (brandInfo.brandState.isPartner) {
            return {
              linkText: this.copyService.getCopy('shared.contactUs'),
              linkType: 'contactUs',
              linkTarget: 'partner',
              linkTestId: 'navItemContactUsLink',
              mobileLinkTestId: 'mobileNavItemContactUsLink',
              mobileMenuItemOrder: 6,
              showOnNavigationMenu: false,
              linkClasses: 'nav-item-contactus-link',
              subLinkArray: this.buildContactSubLinkArray(
                contactDetails?.contactUs,
                contactDetails?.claimsMessage
              )
            };
          } else {
            return {
              linkText: this.copyService.getCopy('shared.contactUs'),
              linkType: 'contactUs',
              linkExternalUrl: env.links.amfamDotComUrl + 'contact?UTM_Source=MyAccount',
              linkTarget: 'amfam',
              linkTestId: 'navItemContactUsLink',
              mobileLinkTestId: 'mobileNavItemContactUsLink',
              mobileMenuItemOrder: 6,
              showOnNavigationMenu: false,
              linkClasses: 'navitem-contactus-link'
            };
          }
        }
      }),
      distinctUntilChanged((prev, curr) => deepEqual(prev, curr))
    );
  }

  private buildAllLinkArrays(): Observable<NavLinkTreeObj[]> {
    return combineLatest([
      this.experienceIdLinkPrefix$,
      this.loggedIn$,
      this.buildBillAccountLinkArray(),
      this.buildRiskLinkArray(),
      this.buildClaimLinkArray(),
      this.buildContactLinkArray()
    ]).pipe(
      map(
        ([
          experienceIdLinkPrefix,
          loggedIn,
          buildBillAccountLinkArray,
          buildRiskLinkArray,
          buildClaimLinkArray,
          contactLinkArray
        ]) => {
          this.isGlobalOutage = this.featureService.isEnabled('global_outage');
          if (!loggedIn) {
            return [];
          } else {
            return [
              {
                linkText: 'Overview',
                linkRoute: experienceIdLinkPrefix + '/overview',
                linkTestId: 'navItemOverviewLink',
                mobileLinkTestId: 'mobileNavItemOverviewLink',
                mobileMenuItemOrder: 1,
                showOnNavigationMenu: true,
                linkClasses: 'nav-item-overview-link'
              },
              ...(buildBillAccountLinkArray ? [buildBillAccountLinkArray] : []),
              ...(buildRiskLinkArray ? [buildRiskLinkArray] : []),
              buildClaimLinkArray,
              this.isGlobalOutage ? ({} as NavLinkTreeObj) : contactLinkArray,
              {
                linkText: 'Profile',
                linkRoute: experienceIdLinkPrefix + '/profile',
                linkTestId: 'navItemProfileLink',
                mobileLinkTestId: 'mobileNavItemProfileLink',
                mobileMenuItemOrder: 5,
                showOnNavigationMenu: true,
                linkClasses: 'nav-item-profile-link'
              },
              {
                linkText: 'Log out',
                clickFunction: () => this.logout(experienceIdLinkPrefix),
                linkTestId: 'navItemLogoutLink',
                mobileLinkTestId: 'mobileNavItemLogoutLink',
                mobileMenuItemOrder: 7,
                showOnNavigationMenu: true,
                linkClasses: 'nav-item-logout-link'
              }
            ];
          }
        }
      )
    );
  }

  private logout = async (experienceIdLinkPrefix: string) => {
    this.spinner.start({ blockActions: true });
    await this.forgerockAuthService.logoutAndRevoke(LOGOUT_ORIGINS.HEADER_MENU);
    this.forgerockAuthService.logout();
    window.location.href = window.location.origin + experienceIdLinkPrefix;
  };
}
